// 🔧 pending_event.js
// ✅ Purpose: Admin-only page to review and activate pending event requests
// ✅ Features:
// - Loads event request from /pendingEvents/{id}
// - Shows form to edit fields and choose status (Pending, Active, Archived)
// - If status is:
//   → Pending: update the existing pendingEvents doc
//   → Archived: mark doc as archived, redirect to events_list
//   → Active: create /users entry if needed, create full /events doc, send email
// - Includes cancel/back button
// - Shows success or error toast at top of page
// - Styled to match events.js layout

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from '../../utils/logger';
import {
  VOTING_TYPE_OPTIONS,
  DEFAULT_VOTING_TYPE,
} from "../../utils/voting_types";
import { EVENT_TYPES } from "../../constants/event_types";
import {
  ATTENDANCE_ESTIMATES,
  HOW_HEARD_OPTIONS,
} from "../../constants/request_intake_options";
import {
  createDefaultEventSeoPages,
} from "../../utils/seo_page_defaults";

const PRICING_TIER_OPTIONS = [
  { value: "starter", label: "Starter Event" },
  { value: "professional", label: "Professional Event" },
  { value: "premium", label: "Premium Event" },
  { value: "enterprise", label: "Enterprise" },
];

const PendingEvents = () => {

  const { id } = useParams(); // Firestore doc ID for pendingEvent
  const navigate = useNavigate();
  const [eventData, setEventData] = useState(null);
  const [status, setStatus] = useState("Pending");
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState("");
  
  // ✅ Load the pending event on first render
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "pendingEvents", id));
        if (snap.exists()) {

          const data = snap.data();

          setEventData({
            votingType: DEFAULT_VOTING_TYPE,
            eventType: "car_show",
            eventWebsite: "",
            eventLocation: "",
            endDate: null,
            eventContact: {
              name: "",
              email: "",
              phone: "",
            },
            requestedModules: [],
            attendanceEstimate: "not_sure",
            howHeardAboutUs: "",
            selectedPlan: "professional",
            selectedPlanName: "Professional Event",
            selectedPlanPriceDisplay: "$149",
            selectedPlanPriceAmount: 149,            
            ...data,
            eventWebsite: data.eventWebsite || data.marketingUrl || "",
            eventContact: {
              name: data.eventContact?.name || data.requester?.name || "",
              email: data.eventContact?.email || data.requester?.email || "",
              phone: data.eventContact?.phone || data.requester?.phone || "",
            },
          });

        } else {
          alert("Event not found.");
          navigate("/events_list");
        }
      } catch (err) {
        logger.error("❌ Failed to load pending event:", {
          value: err
        });
        alert("Failed to load pending event.");
      }
    };
    load();
  }, [id]);

  const handleRequestedModuleChange = (moduleValue) => {
    setEventData((prev) => {
      const currentModules = prev.requestedModules || [];

      const alreadySelected = currentModules.includes(moduleValue);

      return {
        ...prev,
        requestedModules: alreadySelected
          ? currentModules.filter((item) => item !== moduleValue)
          : [...currentModules, moduleValue],
      };
    });
  };

  // ✅ Helper to update form fields
  const handleChange = (e) => {
      const { name, value } = e.target;
      setEventData((prev) => ({ ...prev, [name]: value }));
  };

  // 🔧 Helper to convert Firestore Timestamp → yyyy-mm-dd string for <input type="date">
  const formatDateInput = (timestamp) => {
      if (!timestamp) return "";
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toISOString().split("T")[0];
  };
  
  // 🔧 Helper to convert yyyy-mm-dd → Firestore Timestamp
  const parseDateInput = (value) => {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
  };
  
  // ✅ Updated handleSave in pending_events.js
  // ✅ Purpose: Promote a pending event to an active event with complete collections
  // ✅ Changes:
  // - When event is approved:
  //   - Creates /events/{autoId} document
  //   - Creates subcollections: cars/, categories/, settings/, users/
  //   - Adds settings document under /settings/voting
  //   - Adds manager role to /users
  //   - Clears slug-related localStorage cache
  //   - Deletes original pending event
  const handleSave = async () => {
    if (
        !eventData?.name ||
        !eventData?.eventType ||
        !eventData?.startDate ||
        !eventData?.eventLocation ||
        !eventData?.description
      ) {
      setNotification("❌ Please fill in all required fields.");
      return;
    }

    setSaving(true);

    try {
      const eventRef = doc(db, "pendingEvents", id);

      if (status === "Pending") {
        await updateDoc(eventRef, { ...eventData });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setNotification("✅ Saved changes to pending event.");
        setSaving(false);
        return;
      }

      if (status === "Archived") {
        await updateDoc(eventRef, { archived: true });
        navigate("/pending_events_list?status=Archived");
        return;
      }

      if (status === "Approved") {
        const rawSlug = eventData.name || "";
        const slug = rawSlug.toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^\w\-]/g, "")
          .slice(0, 50);

        if (!slug) {
          setNotification("❌ Event slug could not be generated. Please provide a valid event name.");
          setSaving(false);
          return;
        }

        // 🔍 Look for existing user by email
        const userQuery = query(
          collection(db, "users"),
          where("email", "==", eventData.requester.email)
        );
        const userSnap = await getDocs(userQuery);

        let userId;
        if (!userSnap.empty) {
          userId = userSnap.docs[0].id;
        } else {
          const newUserRef = doc(collection(db, "users"));
          await setDoc(newUserRef, {
            firstName: eventData.requester.firstName,
            lastName: eventData.requester.lastName,
            email: eventData.requester.email,
            phone: eventData.requester.phone || "",
            role: "pending",
            createdAt: serverTimestamp(),
          });
          userId = newUserRef.id;
        }

        // 🏁 Create the event document reference first
        const eventDocRef = doc(collection(db, "events"));

        // 🏁 Create the event
        await setDoc(eventDocRef, {
          name: eventData.name,
          slug,
          description: eventData.description,

          seoPages: createDefaultEventSeoPages(),

          eventType: eventData.eventType || "car_show",
          eventLocation: eventData.eventLocation || "",
          eventWebsite: eventData.eventWebsite || "",
          startDate: eventData.startDate,
          endDate: eventData.endDate || null,
          attendanceEstimate: eventData.attendanceEstimate || "not_sure",
          howHeardAboutUs: eventData.howHeardAboutUs || "",
          eventContact: {
            name: eventData.eventContact?.name || eventData.requester?.name || "",
            email: eventData.eventContact?.email || eventData.requester?.email || "",
            phone: eventData.eventContact?.phone || eventData.requester?.phone || "",
          },
          requestedModules: eventData.requestedModules || [],
          selectedPlan: eventData.selectedPlan || "professional",
          selectedPlanName: eventData.selectedPlanName || "Professional Event",
          selectedPlanPriceDisplay: eventData.selectedPlanPriceDisplay || "$149",
          selectedPlanPriceAmount: eventData.selectedPlanPriceAmount ?? 149,
          paymentStatus: eventData.paymentStatus || "not_paid",
          checkoutStatus: eventData.checkoutStatus || "not_started",          
          createdAt: serverTimestamp(),
          // The invited owner becomes an active manager only after the trusted
          // acceptManagerInvite transaction completes.
          managerIds: [],
          ownerId: userId,
          phone: eventData.eventContact?.phone || eventData.requester?.phone || "",
          votingType: eventData.votingType || DEFAULT_VOTING_TYPE,
          pending: true,
          publicSeo: false,
          privateEvent: true,
          auctionEnabled: (eventData.requestedModules || []).includes("auction"),
          sponsorsEnabled:
            (eventData.requestedModules || []).includes("sponsors"),
          vendorsEnabled:
            (eventData.requestedModules || []).includes("vendors"),
          volunteersEnabled:
            (eventData.requestedModules || []).includes("volunteers"),
          auditEnabled:
            (eventData.requestedModules || []).includes("auditing"),
        });

        const eventId = eventDocRef.id;

        // 🧑 Add event-level manager role
        await setDoc(doc(db, `events/${eventId}/users`, userId), {
          role: "manager",
          pendingManager: true,
          assignedBy: "system",
          assignedAt: serverTimestamp(),
        });

        await setDoc(
          doc(db, "users", userId),
          {
            eventInvites: {
              [eventId]: {
                role: "manager",
                status: "pending",
                eventId,
                eventslug: slug,
                invitedAt: serverTimestamp(),
              },
            },
          },
          { merge: true }
        );

        // 🛠️ Create empty subcollections
        await setDoc(doc(db, `events/${eventId}/settings`, "voting"), {
          votingOpen: false,
          startDate: serverTimestamp(),
          showLeaderboard: true,
          leaderboardLimit: 3,
          bestInShowEnabled: false,
        });

        // 🔔 Trigger the email by setting pending → false
        await updateDoc(eventDocRef, { pending: false });

        // 🧹 Remove pending request
        await deleteDoc(eventRef);

        navigate("/events_list?status=Event Approved");
        return;
      }

      alert("⚠️ Unknown status selected.");
    } catch (err) {
      logger.error("❌ Failed to save event:", {
        value: err
      });
      setNotification("❌ Failed to save event.");
    } finally {
      setSaving(false);
    }
  };
                
  const handleRequesterChange = (e) => {
    const { name, value } = e.target;
    setEventData((prev) => ({
      ...prev,
      requester: {
        ...prev.requester,
        [name]: value,
      },
    }));
  };

  const handleEventContactChange = (e) => {
    const { name, value } = e.target;

    setEventData((prev) => ({
      ...prev,
      eventContact: {
        ...prev.eventContact,
        [name]: value,
      },
    }));
  };  

  if (!eventData) return <FullPageSpinner message="Loading Pending Event..." />;

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">📝 Pending Event</h1>
          <p className="lead text-muted">Review and update the details of a requested event</p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        {notification && <div className="alert alert-success">{notification}</div>}

        <div className="mb-3">
          <label className="form-label">Event Name *</label>
          <input
            name="name"
            className="form-control"
            value={eventData.name || ""}
            onChange={handleChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Event Type *</label>

          <select
            name="eventType"
            className="form-select"
            value={eventData.eventType || "car_show"}
            onChange={handleChange}
          >
            {EVENT_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Selected Tier Package</label>

          <select
            className="form-select"
            value={eventData.selectedPlan || "professional"}
            disabled
          >
            {PRICING_TIER_OPTIONS.map((tier) => (
              <option key={tier.value} value={tier.value}>
                {tier.label}
              </option>
            ))}
          </select>

          <div className="form-text">
            Tier package is selected by the customer during event request and cannot be changed here.
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Estimated Attendance</label>

          <select
            name="attendanceEstimate"
            className="form-select"
            value={eventData.attendanceEstimate || "not_sure"}
            onChange={handleChange}
          >
            {ATTENDANCE_ESTIMATES.map((estimate) => (
              <option key={estimate.value} value={estimate.value}>
                {estimate.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">How did they hear about us?</label>

          <select
            name="howHeardAboutUs"
            className="form-select"
            value={eventData.howHeardAboutUs || ""}
            onChange={handleChange}
          >
            <option value="">Select one</option>

            {HOW_HEARD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Event Date *</label>
          <input
            name="startDate"
            className="form-control"
            type="date"
            value={formatDateInput(eventData.startDate)}
            onChange={(e) =>
              setEventData((prev) => ({
                ...prev,
                startDate: parseDateInput(e.target.value),
              }))
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">End Date</label>
          <input
            name="endDate"
            className="form-control"
            type="date"
            value={formatDateInput(eventData.endDate)}
            onChange={(e) =>
              setEventData((prev) => ({
                ...prev,
                endDate: parseDateInput(e.target.value),
              }))
            }
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Event Location *</label>
          <input
            name="eventLocation"
            className="form-control"
            value={eventData.eventLocation || ""}
            onChange={handleChange}
            placeholder="Venue, city, or city/state"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Event Website</label>
          <input
            name="eventWebsite"
            className="form-control"
            value={eventData.eventWebsite || ""}
            onChange={handleChange}
            placeholder="https://example.com"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Voting Type</label>

          <select
            name="votingType"
            className="form-select"
            value={eventData.votingType || DEFAULT_VOTING_TYPE}
            onChange={handleChange}
          >
            {VOTING_TYPE_OPTIONS.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Add-on Modules Needed</label>

          <div className="border rounded p-3">
            {[
              { value: "volunteers", label: "Volunteer" },
              { value: "sponsors", label: "Sponsor" },
              { value: "auction", label: "Auction" },
              { value: "auditing", label: "Auditing" },
              { value: "vendors", label: "Vendors" },
            ].map((module) => (
              <div className="form-check" key={module.value}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`requested-module-${module.value}`}
                  checked={(eventData.requestedModules || []).includes(module.value)}
                  onChange={() => handleRequestedModuleChange(module.value)}
                />

                <label
                  className="form-check-label"
                  htmlFor={`requested-module-${module.value}`}
                >
                  {module.label}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Description *</label>
          <textarea
            name="description"
            className="form-control"
            rows={3}
            value={eventData.description || ""}
            onChange={handleChange}
          />
        </div>

        <h5 className="mt-4">📇 Event Contact</h5>

        <div className="mb-3">
          <label className="form-label">Event Contact Name</label>
          <input
            name="name"
            className="form-control"
            value={eventData.eventContact?.name || ""}
            onChange={handleEventContactChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Event Contact Email</label>
          <input
            name="email"
            type="email"
            className="form-control"
            value={eventData.eventContact?.email || ""}
            onChange={handleEventContactChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Event Contact Phone</label>
          <input
            name="phone"
            className="form-control"
            value={eventData.eventContact?.phone || ""}
            onChange={handleEventContactChange}
          />
        </div>

        <h5 className="mt-4">📨 Requester Info</h5>

        <div className="mb-3">
          <label className="form-label">First Name *</label>
          <input
            name="firstName"
            className="form-control"
            value={eventData.requester?.firstName || ""}
            onChange={handleRequesterChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Last Name *</label>
          <input
            name="lastName"
            className="form-control"
            value={eventData.requester?.lastName || ""}
            onChange={handleRequesterChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Email *</label>
          <input
            name="email"
            type="email"
            className="form-control"
            value={eventData.requester?.email || ""}
            onChange={handleRequesterChange}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Phone</label>
          <input
            name="phone"
            className="form-control"
            value={eventData.requester?.phone || ""}
            onChange={handleRequesterChange}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">Event Status</label>
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option>Approved</option> 
            <option>Pending</option>
            <option>Archived</option>
          </select>
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-success" onClick={handleSave} disabled={saving}>
            💾 Save
          </button>
          <button className="btn btn-secondary" onClick={() => navigate("/pending_events_list")}>
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
};

export default PendingEvents;
